import { useState, useEffect } from "react";
import API from "../api/axios";
import Layout from "../components/Layout";
import toast from "react-hot-toast";
import { saveToCache, loadFromCache } from "../utils/cache";
import { useTheme } from "../context/ThemeContext";

const CATEGORIES = [
  "Stock Purchase", "Transport", "Electricity", "Rent",
  "Staff Salary", "Equipment", "Marketing", "Packaging",
  "Water", "Miscellaneous",
];

const PAYMENT_METHODS = ["Cash", "Bank Transfer", "Card"];

export default function Expenses() {
  const { theme } = useTheme();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    category: "Stock Purchase",
    description: "",
    amount: "",
    expense_date: "",
    payment_method: "cash",
  });

  // Shared theme styles
  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "14px",
    padding: "20px",
    boxShadow: theme.cardShadow,
  };
  const input = {
    width: "100%",
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: "10px",
    padding: "12px 16px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  };

  const fetchExpenses = async () => {
    if (!navigator.onLine) {
      const cached = loadFromCache("ss_cache_expenses");
      const cachedSummary = loadFromCache("ss_cache_expenses_summary");
      if (cached) setExpenses(cached);
      if (cachedSummary) setSummary(cachedSummary);
      setLoading(false);
      return;
    }
    try {
      const [expRes, sumRes] = await Promise.all([
        API.get("/expenses/"),
        API.get("/expenses/summary"),
      ]);
      if (expRes.data) { setExpenses(expRes.data); saveToCache("ss_cache_expenses", expRes.data); }
      if (sumRes.data) { setSummary(sumRes.data); saveToCache("ss_cache_expenses_summary", sumRes.data); }
    } catch {
      const cached = loadFromCache("ss_cache_expenses");
      if (cached) setExpenses(cached);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/expenses/", {
        ...form,
        amount: parseFloat(form.amount),
        expense_date: form.expense_date || null,
        payment_method: form.payment_method.toLowerCase().replace(" ", "_"),
      });
      toast.success("Expense recorded");
      setForm({ category: "Stock Purchase", description: "", amount: "", expense_date: "", payment_method: "cash" });
      setShowForm(false);
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record expense");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await API.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      fetchExpenses();
    } catch {
      toast.error("Failed to delete expense");
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>
            Expenses
          </h1>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>
            Track your business spending
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{
          background: "linear-gradient(135deg, #e74c3c, #c0392b)",
          color: "#fff", border: "none", borderRadius: "10px",
          padding: "12px 20px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
        }}>
          {showForm ? "Cancel" : "+ Record Expense"}
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          <div style={card}>
            <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "8px", fontWeight: "500" }}>
              Today's Expenses
            </p>
            <p style={{ fontSize: "22px", fontWeight: "700", color: "#e74c3c", letterSpacing: "-0.5px" }}>
              ₦{summary.today.toLocaleString()}
            </p>
          </div>
          <div style={card}>
            <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "8px", fontWeight: "500" }}>
              This Month
            </p>
            <p style={{ fontSize: "22px", fontWeight: "700", color: "#f6a809", letterSpacing: "-0.5px" }}>
              ₦{summary.this_month.toLocaleString()}
            </p>
          </div>
          <div style={card}>
            <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "8px", fontWeight: "500" }}>
              Top Category
            </p>
            <p style={{ fontSize: "16px", fontWeight: "700", color: theme.textSecondary, letterSpacing: "-0.5px" }}>
              {summary.by_category?.[0]?.category || "—"}
            </p>
          </div>
        </div>
      )}

      {/* Add Expense Form */}
      {showForm && (
        <div style={{ ...card, padding: "28px", marginBottom: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "700", color: theme.textPrimary, marginBottom: "20px" }}>
            Record Expense
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", color: theme.textSecondary, display: "block", marginBottom: "6px", fontWeight: "500" }}>
                Category
              </label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={input} required>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", color: theme.textSecondary, display: "block", marginBottom: "6px", fontWeight: "500" }}>
                Description (optional)
              </label>
              <input
                type="text" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. Bought 10 bags of rice from Alaba"
                style={input}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", color: theme.textSecondary, display: "block", marginBottom: "6px", fontWeight: "500" }}>
                Amount (₦)
              </label>
              <input
                type="number" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00" required min="1" style={input}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", color: theme.textSecondary, display: "block", marginBottom: "6px", fontWeight: "500" }}>
                Date (leave blank for today)
              </label>
              <input
                type="date" value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                style={input}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", color: theme.textSecondary, display: "block", marginBottom: "6px", fontWeight: "500" }}>
                Payment Method
              </label>
              <select
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                style={input}
              >
                {PAYMENT_METHODS.map(method => (
                  <option key={method} value={method.toLowerCase().replace(" ", "_")}>{method}</option>
                ))}
              </select>
            </div>

            <button type="submit" disabled={submitting} style={{
              background: "linear-gradient(135deg, #e74c3c, #c0392b)",
              color: "#fff", border: "none", borderRadius: "10px",
              padding: "14px 28px", fontSize: "15px", fontWeight: "700",
              cursor: "pointer", width: "100%", marginTop: "8px",
            }}>
              {submitting ? "Recording..." : "Record Expense"}
            </button>
          </form>
        </div>
      )}

      {/* Expenses Table */}
      {loading ? (
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      ) : expenses.length === 0 ? (
        <div style={{ ...card, padding: "48px 24px", textAlign: "center" }}>
          <p style={{ color: theme.textSecondary }}>
            No expenses recorded yet. Tap Record Expense to start tracking.
          </p>
        </div>
      ) : (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: "14px", overflowX: "auto",
          WebkitOverflowScrolling: "touch", boxShadow: theme.cardShadow,
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
            <thead>
              <tr>
                {["Date", "Category", "Description", "Amount", "Payment", ""].map(h => (
                  <th key={h} style={{
                    textAlign: "left", padding: "12px 16px",
                    fontSize: "12px", fontWeight: "600",
                    color: theme.textSecondary, textTransform: "uppercase",
                    letterSpacing: "0.5px", borderBottom: `1px solid ${theme.divider}`,
                    background: theme.cardBg,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenses.map(exp => (
                <tr key={exp.id} style={{ borderBottom: `1px solid ${theme.divider}` }}>
                  <td style={{ padding: "12px 16px", fontSize: "14px", color: theme.textPrimary }}>
                    {new Date(exp.expense_date).toLocaleDateString("en-NG", {
                      day: "numeric", month: "short", year: "numeric",
                    })}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "14px", color: "#f6a809", fontWeight: 600 }}>
                    {exp.category}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "13px", color: theme.textSecondary }}>
                    {exp.description || "—"}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "14px", color: "#e74c3c", fontWeight: 600 }}>
                    ₦{Number(exp.amount).toLocaleString()}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "14px", color: theme.textPrimary, textTransform: "capitalize" }}>
                    {exp.payment_method?.replace("_", " ")}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <button onClick={() => handleDelete(exp.id)} style={{
                      background: "none", border: "none",
                      cursor: "pointer", fontSize: "16px", padding: "4px",
                    }}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}